import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import InputNumberFormat from 'react-number-format';

import { Close as CloseIcon } from '@styled-icons/evaicons-solid/Close';

import {
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
  VStack,
} from '@chakra-ui/react';

import { formatBRLCurrency } from './validationUtils';

type EditablePriceCellProps = {
  itemId: number;
  value: number;
  originalValue: number;
  isModified: boolean;
  isInvalid: boolean;
  onChange: (nextValue: number) => void;
  onUndo: () => void;
};

const COMMIT_DEBOUNCE_MS = 250;

const EditablePriceCell: React.FC<EditablePriceCellProps> = ({
  itemId,
  value,
  originalValue,
  isModified,
  isInvalid,
  onChange,
  onUndo,
}) => {
  const [draftValue, setDraftValue] = useState<number>(value);
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCommitTimeout = useCallback(() => {
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
      commitTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      clearCommitTimeout();
    };
  }, [clearCommitTimeout]);

  const scheduleChange = useCallback(
    (nextValue: number) => {
      clearCommitTimeout();

      commitTimeoutRef.current = setTimeout(() => {
        onChange(nextValue);
        commitTimeoutRef.current = null;
      }, COMMIT_DEBOUNCE_MS);
    },
    [clearCommitTimeout, onChange],
  );

  const handleBlur = useCallback(() => {
    clearCommitTimeout();
    onChange(draftValue);
  }, [clearCommitTimeout, draftValue, onChange]);

  return (
    <VStack align="flex-end" spacing={1}>
      {isModified && (
        <Text color="red.500" textDecor="line-through" fontSize="xs">
          {formatBRLCurrency(originalValue)}
        </Text>
      )}

      <InputGroup size="sm" width="170px">
        <InputNumberFormat
          type="tel"
          displayType="input"
          thousandSeparator="."
          decimalSeparator=","
          allowNegative={false}
          prefix="R$ "
          decimalScale={2}
          fixedDecimalScale
          value={Number.isFinite(draftValue) ? draftValue : ''}
          onFocus={(event) => event.target.select()}
          onValueChange={(values) => {
            const nextValue = values.floatValue || 0;

            setDraftValue(nextValue);
            scheduleChange(nextValue);
          }}
          onBlur={handleBlur}
          customInput={Input}
          style={{
            borderTopRightRadius: '0',
            borderBottomRightRadius: '0',
            textAlign: 'right',
          }}
          aria-label={`Preço unitário do insumo ${itemId}`}
        />

        <InputRightAddon px={0}>
          <IconButton
            aria-label={`Desfazer alteração do insumo ${itemId}`}
            title="Desfazer alteração"
            size="sm"
            borderTopLeftRadius="0"
            borderBottomLeftRadius="0"
            icon={<Icon as={CloseIcon} w={4} h={4} />}
            onClick={onUndo}
            isDisabled={!isModified}
          />
        </InputRightAddon>
      </InputGroup>

      {isInvalid && (
        <Text color="red.500" fontSize="xs">
          O valor deve ser maior que zero.
        </Text>
      )}
    </VStack>
  );
};

export default memo(EditablePriceCell);
